import React, { useEffect } from 'react';
import { Input } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';
import Helpers from '../../../../../../utils/helper';
import {
    PurchasePriceSummary,
    DeferredFixedPaymentRow,
    DeferredVariablePaymentRow,
    DeferredVariablePaymentType,
} from '../../../../../../slices/marketplace/loi-form/term-form';


export interface TermsFormPriceSummaryBlockProps {
    summary: PurchasePriceSummary | null | undefined;
    total: number;
    onSummaryChange: (patch: Partial<PurchasePriceSummary>) => void;
    earnoutSum: number;
    syntheticJvSum: number;
    readOnly?: boolean;
    errors?: Set<string>;
}

function formatPercent(val: number, total: number): string | null {
    if (total <= 0 || val <= 0) return null;
    return `${Math.round((val / total) * 1000) / 10}%`;
}

const emptyDfpRow = (amount: number | null): DeferredFixedPaymentRow => ({
    paymentTiming: null,
    amount,
    annualizedInterestAccrualRate: null,
    contingent: null,
    contingencyDependentVariableValue: null,
    contDvMinimumValue: null,
    employmentContingency: null,
    tailPeriodForEmployment: null,
});

const emptyDvpRow = (
    valueAtClosing: number | null,
    type?: DeferredVariablePaymentType
): DeferredVariablePaymentRow => ({
    paymentTiming: null,
    type: type ?? null,
    valueAtClosing,
    calculationDependentVariable: null,
    calcDvBaselineValue: null,
    calculationMultiple: null,
    maximumPayment: null,
    employmentContingency: null,
    tailPeriodForEmploymentContingency: null,
    penaltyMultiple: null,
    distributionPeriodicity: null,
});

export const TermsFormPriceSummaryBlock = (props: TermsFormPriceSummaryBlockProps) => {
    const {
        summary,
        total,
        onSummaryChange,
        earnoutSum,
        syntheticJvSum,
        readOnly = false,
        errors,
    } = props;

    const cash = Number(summary?.cashAtCloser?.cashAtClose) || 0;
    const wchValue = Number(summary?.cashAtCloser?.workingCapitalHoldback?.value) || 0;
    const derivedNetCash = summary?.cashAtCloser?.cashAtClose != null ? cash - wchValue : null;
    const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
    const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
    const dfpTotal = dfpRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const dvpTotal = dvpRows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
    const jvVal = Number(summary?.jvEquity?.row?.valueAtClosing) || 0;
    const corpVal = Number(summary?.corporateEquity?.row?.valueAtClosing) || 0;
    const other = Number(summary?.other) || 0;

    useEffect(() => {
        if (derivedNetCash !== (summary?.cashAtCloser?.netCashAtCloser ?? null)) {
            onSummaryChange({
                cashAtCloser: {
                    ...summary?.cashAtCloser,
                    netCashAtCloser: derivedNetCash,
                },
            });
        }
    }, [derivedNetCash, summary?.cashAtCloser, onSummaryChange]);

    return (
        <div className="terms-form-detail-block terms-form-summary-layout terms-form-section-underlined">
            <div className="terms-form-column-headers">
                <div className="terms-form-col-item terms-form-col-header">
                    Purchase Price Consideration Summary, Face Value at Closing
                </div>
                <div className="terms-form-col-value terms-form-col-header-right">Values</div>
                <div className="terms-form-col-percent terms-form-col-header-right">% of Total</div>
            </div>
            <div className="terms-form-form-content">
                <div className="terms-form-summary-row">
                    <div className="terms-form-col-item">Cash at Close</div>
                    <div className="terms-form-col-value">
                        <CurrencyInput
                            name="cashAtCloser.cashAtClose"
                            value={
                                summary?.cashAtCloser?.cashAtClose != null
                                    ? String(summary.cashAtCloser.cashAtClose)
                                    : ''
                            }
                            allowDecimals={true}
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(value) =>
                                onSummaryChange({
                                    cashAtCloser: {
                                        ...summary?.cashAtCloser,
                                        cashAtClose:
                                            value === undefined || value === '' || value === 'NaN'
                                                ? null
                                                : Number(value),
                                    },
                                })
                            }
                            className={`form-control loi-form-input${errors?.has('cashAtClose') ? ' is-invalid' : ''}`}
                            aria-label="Cash at Close"
                        />
                    </div>
                    <div className="terms-form-col-percent terms-form-percent-label">
                        {formatPercent(cash, total) ?? ''}
                    </div>
                </div>
                <div className="terms-form-summary-row terms-form-indent-level-1">
                    <div className="terms-form-col-item terms-form-indented terms-form-wch-inline-label">
                        <span>Working Capital Holdback</span>
                        <Input
                            type="number"
                            value={summary?.cashAtCloser?.workingCapitalHoldback?.monthsHold ?? ''}
                            disabled={readOnly}
                            onChange={(e) =>
                                onSummaryChange({
                                    cashAtCloser: {
                                        ...summary?.cashAtCloser,
                                        workingCapitalHoldback: {
                                            ...summary?.cashAtCloser?.workingCapitalHoldback,
                                            monthsHold:
                                                e.target.value === ''
                                                    ? null
                                                    : Number(e.target.value),
                                        },
                                    },
                                })
                            }
                            invalid={errors?.has('workingCapitalHoldbackMonths')}
                            className="form-control form-control-sm terms-form-inline-months"
                            aria-label="Months Hold"
                        />
                        <span> Months Hold</span>
                    </div>
                    <div className="terms-form-col-value">
                        <CurrencyInput
                            name="cashAtCloser.workingCapitalHoldback.value"
                            value={
                                summary?.cashAtCloser?.workingCapitalHoldback?.value != null
                                    ? String(summary.cashAtCloser.workingCapitalHoldback.value)
                                    : ''
                            }
                            allowDecimals={true}
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(value) =>
                                onSummaryChange({
                                    cashAtCloser: {
                                        ...summary?.cashAtCloser,
                                        workingCapitalHoldback: {
                                            ...summary?.cashAtCloser?.workingCapitalHoldback,
                                            value:
                                                value === undefined || value === '' || value === 'NaN'
                                                    ? null
                                                    : Number(value),
                                        },
                                    },
                                })
                            }
                            className={`form-control loi-form-input${errors?.has('workingCapitalHoldbackValue') ? ' is-invalid' : ''}`}
                            aria-label="Working Capital Holdback"
                        />
                    </div>
                    <div className="terms-form-col-percent terms-form-percent-label" />
                </div>
                <div className="terms-form-summary-row">
                    <div className="terms-form-col-item">Net Cash at Close</div>
                    <div className="terms-form-col-value">
                        <CurrencyInput
                            name="cashAtCloser.netCashAtCloser"
                            value={derivedNetCash != null ? String(derivedNetCash) : ''}
                            allowDecimals={true}
                            allowNegativeValue={true}
                            prefix="$"
                            disabled
                            className="form-control loi-form-input"
                            aria-label="Cash at Close – Net Cash at Close"
                        />
                    </div>
                    <div className="terms-form-col-percent terms-form-percent-label" />
                </div>
                <div className="terms-form-summary-row">
                    <div className="terms-form-col-item">Deferred Fixed Payments</div>
                    <div className="terms-form-col-value">
                        <CurrencyInput
                            name="deferredFixedPayments"
                            value={
                                dfpRows.length && dfpRows[0]?.amount != null
                                    ? String(dfpRows[0].amount)
                                    : ''
                            }
                            allowDecimals={true}
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(value) => {
                                const v =
                                    value === undefined || value === '' || value === 'NaN'
                                        ? null
                                        : Number(value);
                                const next =
                                    dfpRows.length > 0
                                        ? dfpRows.map((r, i) =>
                                              i === 0 ? { ...r, amount: v } : r
                                          )
                                        : [emptyDfpRow(v)];
                                onSummaryChange({
                                    deferredFixedPayments: { rows: next },
                                });
                            }}
                            className={`form-control loi-form-input${errors?.has('deferredFixedPayments') ? ' is-invalid' : ''}`}
                            aria-label="Deferred Fixed Payments"
                        />
                    </div>
                    <div className="terms-form-col-percent terms-form-percent-label">
                        {formatPercent(dfpTotal, total) ?? ''}
                    </div>
                </div>
                <div className="terms-form-summary-row">
                    <div className="terms-form-col-item">Deferred Variable Payments</div>
                    <div className="terms-form-col-value">
                        <CurrencyInput
                            name="deferredVariablePayments"
                            value={
                                dvpTotal !== 0
                                    ? String(dvpTotal)
                                    : (dvpRows[0]?.valueAtClosing != null ? String(dvpRows[0].valueAtClosing) : '')
                            }
                            allowDecimals={true}
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(value) => {
                                const v =
                                    value === undefined || value === '' || value === 'NaN'
                                        ? null
                                        : Number(value);
                                const next =
                                    dvpRows.length > 0
                                        ? dvpRows.map((r, i) =>
                                              i === 0 ? { ...r, valueAtClosing: v } : r
                                          )
                                        : [emptyDvpRow(v, DeferredVariablePaymentType.Earnout)];
                                onSummaryChange({
                                    deferredVariablePayments: { rows: next },
                                });
                            }}
                            className={`form-control loi-form-input${errors?.has('deferredVariablePayments') ? ' is-invalid' : ''}`}
                            aria-label="Deferred Variable Payments"
                        />
                    </div>
                    <div className="terms-form-col-percent terms-form-percent-label">
                        {formatPercent(dvpTotal, total) ?? ''}
                    </div>
                </div>
                {dvpRows.some((r) => r.type === DeferredVariablePaymentType.Earnout) && earnoutSum > 0 && (
                    <div className="terms-form-summary-row terms-form-indent-level-1">
                        <div className="terms-form-col-item terms-form-indented">Earnout</div>
                        <div className="terms-form-col-value">
                            <CurrencyInput
                                name="earnout"
                                value={
                                    (() => {
                                        const earnoutRow = dvpRows.find(
                                            (r) => r.type === DeferredVariablePaymentType.Earnout
                                        );
                                        return earnoutRow?.valueAtClosing != null
                                            ? String(earnoutRow.valueAtClosing)
                                            : '';
                                    })()
                                }
                                allowDecimals={true}
                                allowNegativeValue={false}
                                prefix="$"
                                disabled={readOnly}
                                onValueChange={(value) => {
                                    const v =
                                        value === undefined || value === '' || value === 'NaN'
                                            ? null
                                            : Number(value);
                                    const idx = dvpRows.findIndex(
                                        (r) => r.type === DeferredVariablePaymentType.Earnout
                                    );
                                    const next =
                                        idx >= 0
                                            ? dvpRows.map((r, i) =>
                                                  i === idx
                                                      ? { ...r, valueAtClosing: v }
                                                      : r
                                              )
                                            : [...dvpRows, emptyDvpRow(v, DeferredVariablePaymentType.Earnout)];
                                    onSummaryChange({
                                        deferredVariablePayments: {
                                            rows: next,
                                        },
                                    });
                                }}
                                className="form-control loi-form-input"
                                aria-label="Earnout"
                            />
                        </div>
                        <div className="terms-form-col-percent terms-form-percent-label" />
                    </div>
                )}
                {dvpRows.some((r) => r.type === DeferredVariablePaymentType.Synthetic) && (
                    <div className="terms-form-summary-row terms-form-indent-level-1">
                        <div className="terms-form-col-item terms-form-indented">
                            Synthetic JV Equity
                        </div>
                        <div className="terms-form-col-value">
                            <CurrencyInput
                                name="syntheticJvEquity"
                                value={
                                    (() => {
                                        const sjvRow = dvpRows.find(
                                            (r) => r.type === DeferredVariablePaymentType.Synthetic
                                        );
                                        return sjvRow?.valueAtClosing != null
                                            ? String(sjvRow.valueAtClosing)
                                            : '';
                                    })()
                                }
                                allowDecimals={true}
                                allowNegativeValue={false}
                                prefix="$"
                                disabled={readOnly}
                                onValueChange={(value) => {
                                    const v =
                                        value === undefined || value === '' || value === 'NaN'
                                            ? null
                                            : Number(value);
                                    const idx = dvpRows.findIndex(
                                        (r) => r.type === DeferredVariablePaymentType.Synthetic
                                    );
                                    const next =
                                        idx >= 0
                                            ? dvpRows.map((r, i) =>
                                                  i === idx ? { ...r, valueAtClosing: v } : r
                                              )
                                            : [...dvpRows, emptyDvpRow(v, DeferredVariablePaymentType.Synthetic)];
                                    onSummaryChange({
                                        deferredVariablePayments: {
                                            rows: next,
                                        },
                                    });
                                }}
                                className="form-control loi-form-input"
                                aria-label="Synthetic JV Equity"
                            />
                        </div>
                        <div className="terms-form-col-percent terms-form-percent-label" />
                    </div>
                )}
                <div className="terms-form-summary-row">
                    <div className="terms-form-col-item">JV Equity</div>
                    <div className="terms-form-col-value">
                        <CurrencyInput
                            name="jvEquity"
                            value={
                                summary?.jvEquity?.row?.valueAtClosing !=
                                null
                                    ? String(
                                          summary.jvEquity.row
                                              .valueAtClosing
                                      )
                                    : ''
                            }
                            allowDecimals={true}
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(value) => {
                                const v =
                                    value === undefined || value === '' || value === 'NaN'
                                        ? null
                                        : Number(value);
                                onSummaryChange({
                                    jvEquity: {
                                        row: {
                                            ...summary?.jvEquity?.row,
                                            valueAtClosing: v,
                                        },
                                    },
                                });
                            }}
                            className={`form-control loi-form-input${errors?.has('jvEquityValueAtClosing') ? ' is-invalid' : ''}`}
                            aria-label="JV Equity"
                        />
                    </div>
                    <div className="terms-form-col-percent terms-form-percent-label">
                        {formatPercent(jvVal, total) ?? ''}
                    </div>
                </div>
                <div className="terms-form-summary-row terms-form-summary-row-corporate-equity">
                    <div className="terms-form-col-item">Corporate Equity</div>
                    <div className="terms-form-col-value">
                        <CurrencyInput
                            name="corporateEquity"
                            value={
                                summary?.corporateEquity?.row?.valueAtClosing !=
                                null
                                    ? String(
                                          summary.corporateEquity.row
                                              .valueAtClosing
                                      )
                                    : ''
                            }
                            allowDecimals={true}
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(value) => {
                                const v =
                                    value === undefined || value === '' || value === 'NaN'
                                        ? null
                                        : Number(value);
                                onSummaryChange({
                                    corporateEquity: {
                                        row: {
                                            ...summary?.corporateEquity?.row,
                                            valueAtClosing: v,
                                        },
                                    },
                                });
                            }}
                            className={`form-control loi-form-input${errors?.has('corporateEquityValueAtClosing') ? ' is-invalid' : ''}`}
                            aria-label="Corporate Equity"
                        />
                    </div>
                    <div className="terms-form-col-percent terms-form-percent-label">
                        {formatPercent(corpVal, total) ?? ''}
                    </div>
                </div>
                <div className="terms-form-summary-row">
                    <div className="terms-form-col-item">Other</div>
                    <div className="terms-form-col-value">
                        <CurrencyInput
                            name="other"
                            value={
                                summary?.other != null
                                    ? String(summary.other)
                                    : ''
                            }
                            allowDecimals={true}
                            allowNegativeValue={false}
                            prefix="$"
                            disabled={readOnly}
                            onValueChange={(value) =>
                                onSummaryChange({
                                    other:
                                        value === undefined || value === '' || value === 'NaN'
                                            ? null
                                            : Number(value),
                                })
                            }
                            className={`form-control loi-form-input${errors?.has('other') ? ' is-invalid' : ''}`}
                            aria-label="Other"
                        />
                    </div>
                    <div className="terms-form-col-percent terms-form-percent-label">
                        {formatPercent(other, total) ?? ''}
                    </div>
                </div>
                {total > 0 && (
                    <div className="terms-form-summary-row terms-form-summary-total">
                        <div className="terms-form-col-item">
                            Total Face Value, at Closing
                        </div>
                        <div className="terms-form-col-value">
                            <Input
                                type="text"
                                value={Helpers.currencyFormat(total, false)}
                                disabled
                                readOnly
                                className="form-control loi-form-input"
                                aria-label="Total Face Value at Closing"
                            />
                        </div>
                        <div className="terms-form-col-percent">
                            <Input
                                type="text"
                                value="100%"
                                disabled
                                readOnly
                                className="form-control loi-form-input terms-form-percent-label"
                                aria-label="Percent of Total"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
